import { z } from "zod";
/**
 * The names of a tenant's own Azure resources (ADR 0010, design §3.1): the ONLY source of them.
 *
 * The web derives them once, when the tenant is created in per-tenant mode, and writes them on the
 * tenant row (and the container names on each binding) before anything is deployed; nothing derives
 * them again (ADR 0005 §7). The provisioner passes them to the template, which only checks lengths.
 *
 *   h = base32lower(sha256(environmentName + "\0" + tenantId + "\0" + generation))
 *
 * cut to fill each name to 24 characters, never fewer than 8 (40 bits). The input is the tenant's
 * UUID, never its Name: a deleted tenant's soft-deleted vault cannot block a new tenant, and an
 * outsider cannot predict the names. A collision raises `generation` (1 to 9) and the names are
 * derived again; past 9 is `NAME_EXHAUSTED`.
 *
 * Why never the tenant's Name or Display name, even though a readable name would be easier on
 * the portal (ADR 0012, `docs/adr/0012-tenant-resource-names.md`):
 *   - A vault and a storage account name is PUBLIC DNS. `<account>.blob.core.windows.net` and
 *     `<vault>.vault.azure.net` resolve worldwide, even when the resource sits behind a private
 *     endpoint (the public name then CNAMEs to `privatelink.`), so anyone can enumerate them. A
 *     customer's name in them would publish Skypoint's customer list.
 *   - Azure caps both at 24 characters, lower case and digits for the account, so a name would be
 *     cut and would collide; the hash part keeps the 40 bits the uniqueness needs.
 *   - These names are write-once (ADR 0005 §7, the platform 0035 trigger), and a Display name is
 *     editable: a rename could never follow, and the name would say the old thing for ever.
 * A person finds a tenant's resources by its tags instead: `skydata-tenant-id` (authoritative)
 * and `skydata-tenant-name` (the Display name, a label only; `shared/azure/tenantTags.ts`).
 *
 * Pure: no clock, no randomness, no I/O, and no `node:` import (the client may bundle it, so the
 * hash is `sha256Hex`, pinned against `node:crypto` by its own test). A refusal is a sentence that
 * names the input, never its value.
 */
import { sha256Hex } from "../warehouse/targetTableName";

/** How names are spelled in an environment (design §3.1; `TENANT_RESOURCES_TARGETS.namingProfile`). */
export const NAMING_PROFILES = ["skypoint-eng", "customer-code", "legacy"] as const;
export type NamingProfile = (typeof NAMING_PROFILES)[number];

/**
 * The skypoint-eng vault pattern. No region (decided, Infra Q21, 2026-10-08): the region is the
 * `Region` tag, and keeping it left only 4 hash characters in 24.
 */
export const VAULT_PATTERN_SKYPOINT_ENG = "kv-eng-skdt-<e>-<h>";
/** The skypoint-eng storage pattern keeps the region: `stengskdtdevwu2` leaves 9 hash characters. */
export const STORAGE_PATTERN_SKYPOINT_ENG = "stengskdt<e><r><h>";

/** Azure's limit for both a vault and a storage account name, which every name here fills. */
export const TENANT_RESOURCE_NAME_LENGTH = 24;
/** The fewest hash characters a vault or account name may carry (40 bits, design §3.1). */
export const MIN_HASH_CHARACTERS = 8;
/** The job identity carries a longer hash (its limit is 128, unique per group). */
export const JOB_IDENTITY_HASH_CHARACTERS = 16;
/**
 * A14: the dbt identity's name is the connector (job) identity's plus this suffix. A job identity
 * name always ends in 16 hash characters after its last dash, so no dbt name is ever a job name,
 * and the two change together on a generation bump. Platform 0037 backfills it with the same rule.
 */
export const DBT_IDENTITY_SUFFIX = "-dbt";
/**
 * #514 (owner 2026-10-10, push everywhere): the push identity's name is the connector (job)
 * identity's plus this suffix, by the same rule as the dbt identity's. It reads the tenant's staged
 * files and `wh-` secrets for the Snowflake push, nothing else. Platform 0041 backfills it.
 */
export const PUSH_IDENTITY_SUFFIX = "-push";
/** Generations run 1 to 9 (`tenants_resources_generation_chk`, design §7.1). */
export const MAX_GENERATION = 9;
/** `tenants.job_key`: 6 characters of `[a-z2-7]` (`tenants_job_key_chk`). */
export const JOB_KEY_LENGTH = 6;

/** The two refusals of this module; `NAME_EXHAUSTED` is also a provisioner error code. */
export type TenantResourceNameErrorCode = "NAME_INVALID" | "NAME_EXHAUSTED";

export class TenantResourceNameError extends Error {
  constructor(
    readonly code: TenantResourceNameErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "TenantResourceNameError";
  }
}

export interface TenantResourceNameInput {
  profile: NamingProfile;
  /** The environment's short code (`dev`, `prd`), part of the vault and account names. */
  environmentCode: string;
  /** The placement target's region code (`wu2`); the vault name does not carry it. */
  regionCode: string;
  /** Required by the `customer-code` profile only. */
  customerCode?: string | null;
  /** The tenant's UUID. Never its Name. */
  tenantId: string;
  generation: number;
  /** The deployment's environment name, part of the hash input so environments never share. */
  environmentName: string;
}

export interface TenantResourceNameSet {
  keyVaultName: string;
  storageAccountName: string;
  /** The connector jobs' identity (`tenants.job_identity_name`). */
  jobIdentityName: string;
  /** A14: the dbt job's identity (`tenants.dbt_identity_name`), `<jobIdentityName>-dbt`. */
  dbtIdentityName: string;
  /** #514: the push job's identity (`tenants.push_identity_name`), `<jobIdentityName>-push`. */
  pushIdentityName: string;
  /** The ARM deployment's name, unique per generation (`tenants_resources_deployment_key`). */
  resourcesDeployment: string;
}

const CODE = /^[a-z0-9]+$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const BASE32_ALPHABET = "abcdefghijklmnopqrstuvwxyz234567";

/** RFC 4648 base32, lower case, no padding. */
function base32Lower(bytes: Uint8Array): string {
  let out = "";
  let buffer = 0;
  let bits = 0;
  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += BASE32_ALPHABET[(buffer >>> (bits - 5)) & 31];
      bits -= 5;
    }
    buffer &= (1 << bits) - 1;
  }
  if (bits > 0) out += BASE32_ALPHABET[(buffer << (5 - bits)) & 31];
  return out;
}

function sha256(input: string): Uint8Array {
  const hex = sha256Hex(input);
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++)
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return bytes;
}

function assertGeneration(generation: number): void {
  if (!Number.isInteger(generation) || generation < 1 || generation > MAX_GENERATION) {
    throw new TenantResourceNameError(
      "NAME_INVALID",
      `The resource generation must be a whole number from 1 to ${MAX_GENERATION}.`,
    );
  }
}

/** The fixed part of each name, before the hash; `codes` is checked by the caller. */
function prefixes(
  profile: NamingProfile,
  e: string,
  r: string,
  c: string,
): { vault: string; storage: string; identity: string } {
  switch (profile) {
    case "skypoint-eng":
      return {
        vault: `kv-eng-skdt-${e}-`,
        storage: `stengskdt${e}${r}`,
        identity: `id-eng-skdt-${e}-${r}-`,
      };
    case "customer-code":
      return {
        // #2257: `kvt-<c>-<e>-<h>`, beside the main template's `kv-` and `kvc-` vaults of the same
        // profile. The first pattern, `kv-<c>-skdt-<e>-<h>`, left DDAZ production (`ddaz`, `prod`)
        // 6 hash characters, under the 8 the names need; this one leaves 8 for the longest customer
        // code main.bicep takes (6) with `prod`. No tenant was ever named by the old one.
        vault: `kvt-${c}-${e}-`,
        storage: `st${c}skdt${e}`,
        identity: `id-${c}-skdt-${e}-${r}-`,
      };
    case "legacy":
      // VS dev: `kv-skydata-t-<h11>` and `stskydatat<h14>`, both exactly 24.
      return { vault: "kv-skydata-t-", storage: "stskydatat", identity: "id-skydata-t-" };
  }
}

/**
 * The fixed part of every vault, account and identity name a profile and its codes give (the
 * dbt and push identities start like the connector one): what the hourly orphan scan recognises as one of
 * ours when a resource carries no tenant tag. Lower case.
 */
export function tenantResourceNamePrefixes(codes: NamingCodes): {
  vault: string;
  storage: string;
  identity: string;
} {
  return prefixes(codes.profile, codes.environmentCode, codes.regionCode, codes.customerCode ?? "");
}

export interface NamingCodes {
  profile: NamingProfile;
  environmentCode: string;
  regionCode: string;
  customerCode?: string | null;
}

/**
 * Refuses codes a profile cannot use, or that leave fewer than 8 hash characters in the vault or
 * the account name. Returns the sentence, or null when the codes are fine. Shared by the names,
 * the placement target parser and the web's start checks.
 */
export function namingCodesProblem(codes: NamingCodes): string | null {
  if (!(NAMING_PROFILES as readonly string[]).includes(codes.profile)) {
    return "The naming profile must be skypoint-eng, customer-code or legacy.";
  }
  if (!CODE.test(codes.environmentCode)) {
    return "The environment code must be lower-case letters and digits only.";
  }
  if (!CODE.test(codes.regionCode)) {
    return "The region code must be lower-case letters and digits only.";
  }
  if (codes.profile === "customer-code") {
    if (!codes.customerCode || !CODE.test(codes.customerCode)) {
      return "The customer-code naming profile needs a customer code of lower-case letters and digits only.";
    }
  }
  const p = prefixes(
    codes.profile,
    codes.environmentCode,
    codes.regionCode,
    codes.customerCode ?? "",
  );
  const left = TENANT_RESOURCE_NAME_LENGTH - Math.max(p.vault.length, p.storage.length);
  if (left < MIN_HASH_CHARACTERS) {
    return (
      `The naming profile ${codes.profile} with this environment, region and customer code leaves ` +
      `fewer than ${MIN_HASH_CHARACTERS} hash characters in a 24-character name; use shorter codes.`
    );
  }
  return null;
}

/** The hash characters a profile's vault and account names carry (each at least 8). */
export function hashCharactersLeft(codes: NamingCodes): { vault: number; storage: number } {
  const p = prefixes(
    codes.profile,
    codes.environmentCode,
    codes.regionCode,
    codes.customerCode ?? "",
  );
  return {
    vault: TENANT_RESOURCE_NAME_LENGTH - p.vault.length,
    storage: TENANT_RESOURCE_NAME_LENGTH - p.storage.length,
  };
}

/** Every name of one tenant's resources for one generation (design §3.1). */
export function tenantResourceNames(input: TenantResourceNameInput): TenantResourceNameSet {
  const problem = namingCodesProblem(input);
  if (problem) throw new TenantResourceNameError("NAME_INVALID", problem);
  const tenantId = input.tenantId.toLowerCase();
  if (!UUID.test(tenantId)) {
    throw new TenantResourceNameError("NAME_INVALID", "The tenant id must be a UUID.");
  }
  assertGeneration(input.generation);
  if (input.environmentName.length === 0) {
    throw new TenantResourceNameError("NAME_INVALID", "The environment name must not be empty.");
  }
  const h = base32Lower(
    sha256(`${input.environmentName}\0${tenantId}\0${String(input.generation)}`),
  );
  const p = prefixes(
    input.profile,
    input.environmentCode,
    input.regionCode,
    input.customerCode ?? "",
  );
  const fill = (prefix: string): string =>
    prefix + h.slice(0, TENANT_RESOURCE_NAME_LENGTH - prefix.length);
  const jobIdentityName = p.identity + h.slice(0, JOB_IDENTITY_HASH_CHARACTERS);
  return {
    keyVaultName: fill(p.vault),
    storageAccountName: fill(p.storage),
    jobIdentityName,
    dbtIdentityName: dbtIdentityNameFor(jobIdentityName),
    pushIdentityName: pushIdentityNameFor(jobIdentityName),
    resourcesDeployment: `skydata-tenant-${tenantId}-g${String(input.generation)}`,
  };
}

/** The dbt identity's name for a connector identity's name (A14). */
export function dbtIdentityNameFor(jobIdentityName: string): string {
  return `${jobIdentityName}${DBT_IDENTITY_SUFFIX}`;
}

/** The push identity's name for a connector identity's name (#514). */
export function pushIdentityNameFor(jobIdentityName: string): string {
  return `${jobIdentityName}${PUSH_IDENTITY_SUFFIX}`;
}

/** The longest an instance slug may be inside a container name. */
const CONTAINER_SLUG_MAX = 40;

/**
 * An instance's two containers in its tenant's account: `stg-<s>-<x>` and `lake-<s>-<x>`, where
 * `<s>` is the slug cut to 40 with every run of dashes folded to one and no dash at either end,
 * and `<x>` 6 hex characters of sha256(instanceId). At most 52 of Azure's 63.
 */
export function containerNames(
  instanceSlug: string,
  instanceId: string,
): { staging: string; lake: string } {
  const s = instanceSlug
    .toLowerCase()
    .replaceAll(/[^a-z0-9-]/g, "-")
    .slice(0, CONTAINER_SLUG_MAX)
    .replaceAll(/-{2,}/g, "-")
    .replaceAll(/(?:^-)|(?:-$)/g, "");
  if (s.length === 0) {
    throw new TenantResourceNameError(
      "NAME_INVALID",
      "The instance slug must hold at least one letter or digit.",
    );
  }
  const x = sha256Hex(instanceId.toLowerCase()).slice(0, 6);
  return { staging: `stg-${s}-${x}`, lake: `lake-${s}-${x}` };
}

/**
 * `tenants.job_key` for one attempt: 6 base32 characters of sha256(tenantId + "\0job\0" + attempt).
 * A unique-index collision draws the next attempt. Job names are built from it by the executor
 * (`job-<connector>-<k>`), not here.
 */
export function jobKeyFor(tenantId: string, attempt: number): string {
  if (!Number.isInteger(attempt) || attempt < 0) {
    throw new TenantResourceNameError(
      "NAME_INVALID",
      "The job key attempt must be a whole number, zero or more.",
    );
  }
  return base32Lower(sha256(`${tenantId.toLowerCase()}\0job\0${String(attempt)}`)).slice(
    0,
    JOB_KEY_LENGTH,
  );
}

/** The generation after `g`; past 9 the names are exhausted (`NAME_EXHAUSTED`). */
export function nextGeneration(g: number): number {
  assertGeneration(g);
  if (g === MAX_GENERATION) {
    throw new TenantResourceNameError(
      "NAME_EXHAUSTED",
      `Every resource generation from 1 to ${MAX_GENERATION} is taken for this tenant.`,
    );
  }
  return g + 1;
}

/**
 * The setting is spelled `per-tenant`; the column value is `per_tenant`. The one mapping.
 *
 * ADR 0010 amendment 2026-10-10 (#744): per-tenant is the only supported mode, in every
 * environment, and the default. `shared` stays in the list only as a test-only seam until the
 * shared code paths are retired (the follow-up of #744): `sharedModeRefusal` refuses it everywhere
 * but the test suite, and always in a deployed environment.
 */
export const TENANT_RESOURCES_MODE_SETTINGS = ["shared", "per-tenant"] as const;
export type TenantResourcesModeSetting = (typeof TENANT_RESOURCES_MODE_SETTINGS)[number];

/** What an unset `TENANT_RESOURCES_MODE` means: never `shared` (#744). */
export const DEFAULT_TENANT_RESOURCES_MODE: TenantResourcesModeSetting = "per-tenant";

/**
 * The one parse of `TENANT_RESOURCES_MODE`, for the web and the runner (`server/config.ts`), the
 * provisioner, skyMonitor and the dev seed alike, so every process accepts and refuses the same
 * values: unset is per-tenant, anything but the two spellings (an empty value, a stray space, a
 * different case) is refused. `sharedModeRefusal` then decides whether `shared` may start.
 */
export const tenantResourcesModeSettingSchema = z
  .enum(TENANT_RESOURCES_MODE_SETTINGS)
  .default(DEFAULT_TENANT_RESOURCES_MODE);

/**
 * Why `TENANT_RESOURCES_MODE=shared` may not start this process, or null when it may (#744). One
 * rule for the web, the runner, the provisioner and skyMonitor: `shared` is refused in a deployed
 * environment (`deployed`: NODE_ENV=production, JOB_EXECUTOR=containerapps or
 * SECRET_STORE=keyvault), and anywhere outside the test suite (NODE_ENV=test), so a local run is
 * per-tenant over the fake provisioner too. Each refusal names the variable, never a value.
 */
export function sharedModeRefusal(input: {
  mode: string | undefined;
  deployed: boolean;
  nodeEnv: string | undefined;
}): string | null {
  if (input.mode !== "shared") return null;
  if (input.deployed) {
    return (
      "TENANT_RESOURCES_MODE=shared is refused in a deployed environment (NODE_ENV=production, " +
      "JOB_EXECUTOR=containerapps or SECRET_STORE=keyvault): every environment runs per-tenant " +
      "(ADR 0010 amendment 2026-10-10, #744). Set TENANT_RESOURCES_MODE=per-tenant, or leave it " +
      "unset, with TENANT_RESOURCES_TARGETS naming at least one placement target"
    );
  }
  if (input.nodeEnv !== "test") {
    return (
      "TENANT_RESOURCES_MODE=shared is refused outside the test suite (NODE_ENV=test): a local run " +
      "is per-tenant over the fake provisioner (TENANT_PROVISIONER_ARM=fake and a local " +
      "TENANT_RESOURCES_TARGETS, .env.example; #744)"
    );
  }
  return null;
}

export function resourcesModeFor(setting: TenantResourcesModeSetting): "shared" | "per_tenant" {
  return setting === "per-tenant" ? "per_tenant" : "shared";
}

/**
 * A tenant's private endpoints (design §3.1): `pe-<vault or account name>-{vault,blob,queue,dfs}`
 * (the queue's for the tenant's own progress queue, A16).
 */
export function endpointNames(
  names: Pick<TenantResourceNameSet, "keyVaultName" | "storageAccountName">,
): {
  vault: string;
  blob: string;
  queue: string;
  dfs: string;
} {
  return {
    vault: `pe-${names.keyVaultName}-vault`,
    blob: `pe-${names.storageAccountName}-blob`,
    queue: `pe-${names.storageAccountName}-queue`,
    dfs: `pe-${names.storageAccountName}-dfs`,
  };
}
